import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Card, Typography, Grid, Box, Button, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, Chip,
    CircularProgress, Alert, IconButton, TextField, InputAdornment,
    FormControl, InputLabel, Select, MenuItem, TablePagination, Avatar
} from '@mui/material';
import { useNotify } from 'react-admin';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import EditIcon from '@mui/icons-material/Edit';
import InfoIcon from '@mui/icons-material/Info';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ClearIcon from '@mui/icons-material/Clear';
import EuroIcon from '@mui/icons-material/Euro';
import { motion } from 'framer-motion';
import axios from 'axios';
import config from '../../config/config.json';

const host = config.production.host;
const getJwt = () => localStorage.getItem("feathers-jwt");

function PianiPage() {
    const navigate = useNavigate();
    const notify = useNotify();
    const [piani, setPiani] = React.useState([]);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState(null);
    const [searchTerm, setSearchTerm] = React.useState('');
    const [filterStatus, setFilterStatus] = React.useState('all');
    const [page, setPage] = React.useState(0);
    const [limit, setLimit] = React.useState(25);
    const [total, setTotal] = React.useState(0);
    const [gruppi, setGruppi] = React.useState([]);

    const buildQuery = () => {
        const query = { $limit: limit, $skip: page * limit, $sort: { nome: 1 } };
        const filters = {};

        if (searchTerm.trim()) {
            const search = `%${searchTerm.trim()}%`;
            filters.$or = [
                { nome: { $like: search } },
                { DescrizioneCompleta: { $like: search } },
                { note: { $like: search } }
            ];
        }

        if (filterStatus !== 'all') {
            switch (filterStatus) {
                case 'active': filters.flAttivo = 1; filters.flAnnullato = 0; break;
                case 'inactive': filters.flAttivo = 0; break;
                case 'hidden': filters.flVisibile = 0; break;
                case 'cancelled': filters.flAnnullato = 1; break;
            }
        }

        if (Object.keys(filters).length > 0) Object.assign(query, filters);
        return query;
    };

    const loadGruppi = async () => {
        try {
            const jwt = getJwt();
            const response = await axios.get(`${host}/tbl-gruppo`, {
                headers: { 'Authorization': `Bearer ${jwt}` },
                params: { $sort: { nome: 1 } }
            });
            setGruppi(response.data.data || []);
        } catch (error) {
            console.error("Errore caricamento gruppi:", error);
        }
    };

    const loadPiani = async () => {
        try {
            setLoading(true);
            setError(null);
            const jwt = getJwt();
            const query = buildQuery();
            const response = await axios.get(`${host}/tbl-piani`, {
                headers: { 'Authorization': `Bearer ${jwt}` },
                params: query
            });
            setPiani(response.data.data || []);
            setTotal(response.data.total || 0);
        } catch (error) {
            console.error("Errore caricamento piani:", error);
            setError("Errore nel caricamento dei piani");
            notify("Errore nel caricamento dei piani", { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const getGruppoName = (gruppoId) => {
        if (!gruppoId) return '-';
        const gruppo = gruppi.find(g => g.id === gruppoId);
        return gruppo ? gruppo.nome : `ID: ${gruppoId}`;
    };

    /* const handleToggleVisibility = async (piano) => {
        try {
            const jwt = getJwt();
            await axios.patch(`${host}/tbl-piani/${piano.id}`,
                { flVisibile: piano.flVisibile ? 0 : 1 },
                { headers: { 'Authorization': `Bearer ${jwt}` } }
            );
            notify(`Piano ${piano.flVisibile ? 'nascosto' : 'mostrato'}`, { type: 'success' });
            loadPiani();
        } catch (error) {
            notify("Errore nell'aggiornamento", { type: 'error' });
        }
    }; */

    const resetFilters = () => {
        setSearchTerm('');
        setFilterStatus('all');
        setPage(0);
    };

    React.useEffect(() => {
        loadGruppi();
    }, []);

    React.useEffect(() => {
        loadPiani();
    }, [page, limit, searchTerm, filterStatus]);

    const stats = {
        totali: total,
        attivi: piani.filter(p => p.flAttivo && !p.flAnnullato).length,
        inattivi: piani.filter(p => !p.flAttivo).length
    };

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#f6f7fb', minHeight: '100vh' }}>
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>

                <Box sx={{ mb: 3 }}>
                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                        📦 Gestione Piani
                    </Typography>
                    <Card sx={{ p: 3, borderRadius: 3, boxShadow: 2 }}>
                        <Button
                            fullWidth
                            variant="outlined"
                            startIcon={<AddCircleIcon />}
                            sx={{
                                color: '#4A90E2',
                                borderColor: '#4A90E2',
                                fontWeight: 600,
                                borderRadius: 2,
                                py: 1.5,
                                '&:hover': { borderColor: '#357ABD', bgcolor: 'rgba(74, 144, 226, 0.04)' }
                            }}
                            onClick={() => navigate('/piani/create')}>
                            Nuovo Piano
                        </Button>
                    </Card>
                </Box>

                <Card sx={{ p: 2, mb: 3, borderRadius: 3, boxShadow: 2 }}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder="Cerca piano..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                InputProps={{
                                    startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
                                    endAdornment: searchTerm && (
                                        <InputAdornment position="end">
                                            <IconButton size="small" onClick={() => setSearchTerm('')}>
                                                <ClearIcon fontSize="small" />
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                            />
                        </Grid>
                        <Grid item xs={6} md={3}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Stato</InputLabel>
                                <Select value={filterStatus} label="Stato" onChange={(e) => { setFilterStatus(e.target.value); setPage(0); }}>
                                    <MenuItem value="all">Tutti</MenuItem>
                                    <MenuItem value="active">Attivi</MenuItem>
                                    <MenuItem value="inactive">Inattivi</MenuItem>
                                    <MenuItem value="hidden">Nascosti</MenuItem>
                                    <MenuItem value="cancelled">Annullati</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={6} md={5}>
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                <Button size="small" variant="outlined" onClick={resetFilters} disabled={searchTerm === '' && filterStatus === 'all'}>
                                    Cancella filtri
                                </Button>
                                <Button size="small" variant="outlined" startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />} onClick={loadPiani} disabled={loading}>
                                    Aggiorna
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </Card>

                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={4}>
                        <Card sx={{ p: 2, textAlign: 'center', borderRadius: 2 }}>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: '#4A90E2' }}>{stats.totali}</Typography>
                            <Typography variant="body2" color="text.secondary">Piani Totali</Typography>
                        </Card>
                    </Grid>
                    <Grid item xs={4}>
                        <Card sx={{ p: 2, textAlign: 'center', borderRadius: 2 }}>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: '#4caf50' }}>{stats.attivi}</Typography>
                            <Typography variant="body2" color="text.secondary">Attivi</Typography>
                        </Card>
                    </Grid>
                    <Grid item xs={4}>
                        <Card sx={{ p: 2, textAlign: 'center', borderRadius: 2 }}>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: '#ff9800' }}>{stats.inattivi}</Typography>
                            <Typography variant="body2" color="text.secondary">Inattivi</Typography>
                        </Card>
                    </Grid>
                </Grid>

                <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
                    {error && <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>}
                    {loading && !piani.length ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
                            <CircularProgress size={40} />
                        </Box>
                    ) : piani.length > 0 ? (
                        <>
                            <TableContainer sx={{ maxHeight: 600 }}>
                                <Table stickyHeader size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 600 }}>Nome</TableCell>
                                            <TableCell align="center" sx={{ fontWeight: 600 }}>Gruppo</TableCell>
                                            <TableCell align="center" sx={{ fontWeight: 600 }}>Prezzi</TableCell>
                                            <TableCell align="center" sx={{ fontWeight: 600 }}>Max Squadre</TableCell>
                                            <TableCell align="center" sx={{ fontWeight: 600 }}>Stato</TableCell>
                                            <TableCell align="center" sx={{ fontWeight: 600 }}>Azioni</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {piani.map((piano, index) => {
                                            let status = 'Attivo', color = '#4caf50', bgcolor = '#e8f5e8';
                                            if (piano.flAnnullato) { status = 'Annullato'; color = '#f44336'; bgcolor = '#ffebee'; }
                                            else if (!piano.flAttivo) { status = 'Inattivo'; color = '#ff9800'; bgcolor = '#fff3e0'; }
                                            else if (!piano.flVisibile) { status = 'Nascosto'; color = '#9e9e9e'; bgcolor = '#f5f5f5'; }

                                            return (
                                                <TableRow key={piano.id} sx={{ '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' }, bgcolor: index % 2 === 0 ? 'rgba(0, 0, 0, 0.02)' : 'inherit' }}>
                                                    <TableCell>
                                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{piano.nome || 'N/A'}</Typography>
                                                        {piano.DescrizioneCompleta && (
                                                            <Typography variant="caption" color="text.secondary">
                                                                {piano.DescrizioneCompleta.length > 40 ? `${piano.DescrizioneCompleta.substring(0, 40)}...` : piano.DescrizioneCompleta}
                                                            </Typography>
                                                        )}
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        {piano.gruppoId ? (
                                                            <Chip
                                                                label={getGruppoName(piano.gruppoId)}
                                                                size="small"
                                                                sx={{ bgcolor: '#e3f2fd', color: '#1976d2', fontWeight: 500 }}
                                                            />
                                                        ) : '-'}
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                                                            {piano.prezzoMensile != null && (
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                    <EuroIcon sx={{ fontSize: 14, color: '#4caf50' }} />
                                                                    <Typography variant="body2">{piano.prezzoMensile}/mese</Typography>
                                                                </Box>
                                                            )}
                                                            {piano.prezzoAnnuale != null && (
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                    <EuroIcon sx={{ fontSize: 14, color: '#2196f3' }} />
                                                                    <Typography variant="caption" color="text.secondary">{piano.prezzoAnnuale}/anno</Typography>
                                                                </Box>
                                                            )}
                                                            {piano.prezziPersonalizzati && Array.isArray(piano.prezziPersonalizzati) && piano.prezziPersonalizzati.map((p, idx) => (
                                                                <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                    <EuroIcon sx={{ fontSize: 14, color: '#ff9800' }} />
                                                                    <Typography variant="caption" color="text.secondary">{p.prezzo}/{p.mesi}m</Typography>
                                                                </Box>
                                                            ))}
                                                            {!piano.prezzoMensile && !piano.prezzoAnnuale && (!piano.prezziPersonalizzati || piano.prezziPersonalizzati.length === 0) && '-'}
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell align="center">{piano.maxNumeroSquadre || '-'}</TableCell>
                                                    <TableCell align="center">
                                                        <Chip label={status} size="small" sx={{ color, bgcolor, fontWeight: 500 }} />
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                                                            <Button
                                                                variant="outlined"
                                                                size="small"
                                                                onClick={() => navigate(`/piani/${piano.id}/show`)}
                                                                startIcon={<InfoIcon />}
                                                            >
                                                                Dettaglio
                                                            </Button>
                                                            <Button
                                                                variant="contained"
                                                                size="small"
                                                                onClick={() => navigate(`/piani/${piano.id}/edit`)}
                                                                startIcon={<EditIcon />}
                                                            >
                                                                Modifica
                                                            </Button>
                                                            {/* <IconButton size="small" onClick={() => handleToggleVisibility(piano)} color={piano.flVisibile ? "default" : "warning"}>
                                                                {piano.flVisibile ? <VisibilityIcon fontSize="small" /> : <VisibilityOffIcon fontSize="small" />}
                                                            </IconButton> */}
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <TablePagination
                                component="div"
                                count={total}
                                page={page}
                                onPageChange={(e, newPage) => setPage(newPage)}
                                rowsPerPage={limit}
                                onRowsPerPageChange={(e) => { setLimit(parseInt(e.target.value, 10)); setPage(0); }}
                                rowsPerPageOptions={[10, 25, 50, 100]}
                                labelRowsPerPage="Righe per pagina:"
                                labelDisplayedRows={({ from, to, count }) => `${from}-${to} di ${count}`}
                            />
                        </>
                    ) : (
                        <Box sx={{ textAlign: 'center', p: 6 }}>
                            <Typography variant="h6" color="text.secondary">Nessun piano trovato</Typography>
                        </Box>
                    )}
                </Card>
            </motion.div>
        </Box>
    );
}

export default PianiPage;
